import { getNetworkMembership, getUserRoles } from './authRoles';

const normalizeKey = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : null);

const parsePermissionEntry = (entry) => {
  if (typeof entry === 'string') return normalizeKey(entry);
  if (!entry || typeof entry !== 'object') return null;

  return (
    normalizeKey(entry.key) ||
    normalizeKey(entry.name) ||
    normalizeKey(entry.permission) ||
    normalizeKey(entry.value)
  );
};

export const getUserPermissions = (user) => {
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  return permissions.map(parsePermissionEntry).filter(Boolean);
};

export const hasRole = (user, role) => {
  const normalizedRole = normalizeKey(role);
  if (!normalizedRole) return false;
  return (getUserRoles(user) || []).includes(normalizedRole);
};

export const hasPermission = (user, permission) => {
  const normalizedPermission = normalizeKey(permission);
  if (!normalizedPermission) return false;
  return getUserPermissions(user).includes(normalizedPermission);
};

export const hasAnyPermission = (user, permissions = []) =>
  permissions.some((permission) => hasPermission(user, permission));

export const getNetworkPermissions = (user, networkId) => {
  const membership = getNetworkMembership(user, networkId);
  return Array.isArray(membership?.permissions) ? membership.permissions : [];
};

export const hasNetworkPermission = (user, networkId, permission) => {
  const normalizedPermission = normalizeKey(permission);
  if (!normalizedPermission) return false;
  return getNetworkPermissions(user, networkId).includes(normalizedPermission);
};

export const canViewNetwork = (user, networkId) => {
  const membership = getNetworkMembership(user, networkId);
  if (!membership) return false;

  return (
    hasNetworkPermission(user, networkId, 'view_network') ||
    hasNetworkPermission(user, networkId, 'manage_network') ||
    ['network_manager', 'network_admin', 'network_viewer', 'network_user', 'network_member'].includes(membership.role_name)
  );
};

export const canManageNetwork = (user, networkId) => {
  const membership = getNetworkMembership(user, networkId);
  if (!membership) return false;

  return (
    hasNetworkPermission(user, networkId, 'manage_network') ||
    ['network_manager', 'network_admin'].includes(membership.role_name) ||
    membership.context_role === 'admin'
  );
};

const ADMIN_SECTION_PERMISSIONS = [
  'manage_permissions',
  'manage_users',
  'manage_leads',
  'view_leads',
];

export const canAccessAdminSection = (user) =>
  hasRole(user, 'hke_admin') || hasAnyPermission(user, ADMIN_SECTION_PERMISSIONS);

export const canViewFeatureToggles = (user) =>
  hasRole(user, 'hke_admin') || hasPermission(user, 'manage_permissions');

export const canViewLeadsAdmin = (user) =>
  hasRole(user, 'hke_admin') || hasAnyPermission(user, ['view_leads', 'manage_leads']);

export const canManageConsultantsAdmin = (user) =>
  hasRole(user, 'hke_admin') || hasPermission(user, 'manage_users');
